import React, { useEffect, useMemo, useState } from 'react';
import { LineChart } from '../../components/charts';

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}
function monthKey(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function fmtPct(v) {
  const n = Number(v || 0);
  return `${(n * 100).toFixed(2)}%`;
}
function formatNumber(n) {
  const v = Number(n || 0);
  if (isNaN(v)) return '0';
  return v.toLocaleString('en-US');
}

// Helper to format 'YYYY-MM' to 'MMM YYYY' (e.g., 'Jun 2025')
function formatMonthHeader(ym) {
  if (!ym || typeof ym !== 'string') return ym;
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// --- Smart brand / non-brand detection helpers ---
function escapeRegExp(s) {
  return String(s || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Common TR/EN stop-words to exclude from auto-detection
const BRAND_STOP_WORDS = new Set([
  // Turkish
  've', 'ile', 'için', 'bir', 'bu', 'şu', 'ne', 'mi', 'mı', 'mu', 'mü',
  'gibi', 'kadar', 'ama', 'fakat', 'da', 'de', 'olan', 'var', 'yok',
  'en', 'çok', 'az', 'her', 'tüm', 'nasıl',
  // English
  'the', 'a', 'an', 'and', 'or', 'of', 'in', 'for', 'with', 'on', 'to',
  'my', 'is', 'are', 'how', 'what', 'when', 'where', 'why', 'best'
]);

function tokenizeQuery(q) {
  return String(q || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .split(/\s+/)
    .filter(t => t.length >= 2 && !BRAND_STOP_WORDS.has(t));
}

// Auto-detect brand terms: look for tokens shared across top-10 click queries.
// Fall back to the first meaningful token of the top-1 query.
function autoDetectBrandTerms(queryTotals) {
  const entries = Object.entries(queryTotals || {});
  if (!entries.length) return [];
  const top = entries
    .sort((a, b) => (Number(b[1].clicks || 0)) - (Number(a[1].clicks || 0)))
    .slice(0, 10)
    .map(([q]) => q);
  if (!top.length) return [];
  const tokenSets = top.map(q => new Set(tokenizeQuery(q)));
  const freq = new Map();
  for (const set of tokenSets) {
    for (const t of set) freq.set(t, (freq.get(t) || 0) + 1);
  }
  // A token is a brand candidate if it appears in at least half of the top queries
  const threshold = Math.max(3, Math.ceil(top.length / 2));
  const common = [...freq.entries()]
    .filter(([, n]) => n >= threshold)
    .sort((a, b) => b[1] - a[1])
    .map(([t]) => t);
  if (common.length) return common.slice(0, 3);
  const fallback = tokenizeQuery(top[0] || '');
  return fallback.slice(0, 1);
}

// Build a regex that matches any of the terms with a word-prefix boundary,
// so "high5" matches "high5", "high5'in", "high5 türkiye", "high5ler" but NOT
// "ayhigh5" or "hightower" (because 'a'/'t' before 'high5' is a letter).
function buildBrandRegex(terms) {
  const clean = (terms || [])
    .map(t => String(t || '').trim().toLowerCase())
    .filter(Boolean);
  if (!clean.length) return null;
  const escaped = clean.map(escapeRegExp);
  try {
    return new RegExp(`(^|[^\\p{L}\\p{N}])(${escaped.join('|')})`, 'iu');
  } catch (e) {
    // Fallback for very old browsers without Unicode property escapes
    return new RegExp(`(^|[^a-z0-9])(${escaped.join('|')})`, 'i');
  }
}

function isBrandQuery(q, regex) {
  if (!regex) return false;
  return regex.test(String(q || '').toLowerCase());
}

// Landing-page URLs share a host and are long; the path is the part that
// distinguishes them. The full URL stays in the title attribute and the link.
//
// `mainOrigin` keeps the origin visible for pages that are not on the group's
// dominant one. Search Console reports http/https and www/non-www variants as
// separate pages, so stripping the origin renders genuinely different rows as
// an identical "/" — comparing on the origin rather than the host is what
// keeps the http:// variant distinguishable.
function shortenPage(url, mainOrigin) {
  const s = String(url || '');
  try {
    const u = new URL(s);
    const path = (u.pathname === '/' ? '/' : u.pathname.replace(/\/$/, '')) + (u.search || '');
    return u.origin === mainOrigin ? path : `${u.origin}${path}`;
  } catch {
    return s;
  }
}

// The origin most of a query's pages sit on; the rest keep theirs on screen.
function dominantOrigin(pages) {
  const counts = new Map();
  for (const p of pages || []) {
    try {
      const o = new URL(p.page).origin;
      counts.set(o, (counts.get(o) || 0) + 1);
    } catch { /* not a URL — nothing to strip */ }
  }
  let best = null, bestN = 0;
  for (const [o, n] of counts) if (n > bestN) { best = o; bestN = n; }
  return best;
}

function fmtDateShort(d) {
  if (!d) return '—';
  const dt = new Date(`${String(d).slice(0, 10)}T00:00:00Z`);
  if (isNaN(dt)) return String(d).slice(0, 10);
  return dt.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// Mirrors the synthetic Skechers data the rest of the page uses for demo brands.
function buildDemoQueryPages() {
  const host = 'https://www.example-store.com';
  const spec = [
    ['spor ayakkabı', 412000, ['/erkek-spor-ayakkabi', '/kadin-spor-ayakkabi', '/', '/outlet/spor-ayakkabi']],
    ['koşu ayakkabısı', 268000, ['/kosu-ayakkabisi', '/erkek-kosu', '/kadin-kosu', '/']],
    ['yürüyüş ayakkabısı', 194000, ['/yuruyus-ayakkabisi', '/outdoor', '/']],
    ['beyaz spor ayakkabı', 151000, ['/beyaz-spor-ayakkabi', '/kadin-spor-ayakkabi', '/']],
    ['ortopedik ayakkabı', 122000, ['/ortopedik-ayakkabi', '/memory-foam', '/']],
    ['sneaker', 98000, ['/sneaker', '/erkek-sneaker', '/kadin-sneaker', '/']],
  ];
  return spec.map(([query, impressions, paths]) => {
    const weights = paths.map((_, i) => Math.pow(0.45, i));
    const sum = weights.reduce((a, b) => a + b, 0);
    const pages = paths.map((p, i) => {
      const impr = Math.round(impressions * (weights[i] / sum));
      const clicks = Math.round(impr * 0.041);
      return {
        page: host + p, impressions: impr, clicks,
        ctr: impr > 0 ? clicks / impr : 0,
        position: 4.2 + i * 0.9,
        share: impr / impressions
      };
    });
    const clicks = pages.reduce((a, p) => a + p.clicks, 0);
    return {
      query, impressions, clicks,
      ctr: clicks / impressions,
      position: 5.1,
      pageCount: paths.length + 12,
      reportedImpressions: Math.round(impressions * 0.99),
      coverage: 1.01,
      pages
    };
  });
}

export default function MonthlyQueries() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const baseHeaders = { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) };
  const DEMO_BRAND_IDS_SC = new Set(['2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a', 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4']);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  // nbMonthlyAgg is the "quick-paint" fallback populated from the backend brand-split view.
  // Once `queryTotals` / `queryAgg` finish loading, we derive the final non-brand monthly
  // aggregation from the raw query data using the user's brand terms (see `nbMonthlyAggDisplay`).
  const [nbMonthlyAgg, setNbMonthlyAgg] = useState([]);
  const [queryMonths, setQueryMonths] = useState([]);
  const [queryAgg, setQueryAgg] = useState({});
  const [queryTotals, setQueryTotals] = useState({}); // { [query]: { clicks, impressions } }
  const [topQueries, setTopQueries] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [nbChartsLoading, setNbChartsLoading] = useState(true);
  const [queriesLoading, setQueriesLoading] = useState(true);

  // Smart brand-term detection state
  const [brandTerms, setBrandTerms] = useState([]);
  const [brandTermsInitialized, setBrandTermsInitialized] = useState(false);
  const [editingBrand, setEditingBrand] = useState(false);
  const [newBrandTerm, setNewBrandTerm] = useState('');

  // Non-brand query -> landing page breakdown
  const [qpRows, setQpRows] = useState([]);
  const [qpLoading, setQpLoading] = useState(false);
  const [qpError, setQpError] = useState(null);
  const [qpCoverage, setQpCoverage] = useState({ minDate: null, maxDate: null });
  const [qpUnavailable, setQpUnavailable] = useState(false);
  const [qpRange, setQpRange] = useState({ startDate: null, endDate: null });
  const [qpCollapsed, setQpCollapsed] = useState(() => new Set());

  // Quick: fill charts first from monthly views
  const fetchChartsQuick = async () => {
    try {
      const startDate = '2000-01-01';
      const d = new Date(); d.setDate(d.getDate() - 1);
      const endDate = d.toISOString().slice(0, 10);
      const params = new URLSearchParams({ startDate, endDate, limit: '100000' });
      // DEMO: Skechers-themed monthly trend for specific brand
      if (DEMO_BRAND_IDS_SC.has(brandId)) {
        const months = [];
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
          const dt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
          months.push(dt.toISOString().slice(0, 10));
        }
        const demoRows = months.map((m, idx) => {
          const clicks = 9000 + idx * 700; // growing
          const impr = 380000 + idx * 24000;
          const pos = 7.5 - Math.min(3.0, idx * 0.12);
          return { month: m, clicks, impressions: impr, position: pos };
        });
        setRows(demoRows);
        const nbAgg = months.map((m, idx) => {
          // assume ~55% non-brand share
          const clicks = Math.round((9000 + idx * 700) * 0.55);
          const impressions = Math.round((380000 + idx * 24000) * 0.58);
          return { month: m, clicks, impressions };
        });
        setNbMonthlyAgg(nbAgg);
        setChartsLoading(false);
        setNbChartsLoading(false);
        return;
      }
      // totals-monthly for charts
      const resTM = await fetch(`https://api.glowytics.com/api/google-ads/search-console/totals-monthly?${params}`, {
        credentials: 'include',
        headers: baseHeaders
      });
      const jsTM = await resTM.json();
      if (!resTM.ok) throw new Error(jsTM.error || 'Failed to fetch totals monthly');
      setRows(Array.isArray(jsTM.rows) ? jsTM.rows : []);
      setChartsLoading(false);
      // brand-split for non-brand charts
      try {
        const resBS = await fetch(`https://api.glowytics.com/api/google-ads/search-console/brand-split-monthly?${params}`, {
          credentials: 'include',
          headers: baseHeaders
        });
        const jsBS = await resBS.json();
        if (!resBS.ok) throw new Error(jsBS.error || 'Failed to fetch brand split monthly');
        const rowsBS = Array.isArray(jsBS.rows) ? jsBS.rows : [];
        const nbByMonth = new Map();
        for (const r of rowsBS) {
          if (String(r.group_label || '').toUpperCase() !== 'NON_BRAND') continue;
          const m = typeof r.month === 'string' ? r.month.slice(0, 10) : r.month;
          const c = Number(r.clicks || 0); const i = Number(r.impressions || 0);
          if (!nbByMonth.has(m)) nbByMonth.set(m, { clicks: 0, impressions: 0 });
          const prev = nbByMonth.get(m);
          nbByMonth.set(m, { clicks: prev.clicks + c, impressions: prev.impressions + i });
        }
        const months = [...nbByMonth.keys()].sort((a,b)=>(a>b?-1:1));
        setNbMonthlyAgg(months.map(m => ({ month: m, ...nbByMonth.get(m) })));
      } finally {
        setNbChartsLoading(false);
      }
    } catch (e) {
      console.error('[MonthlyQueries] fetchChartsQuick error:', e);
      setChartsLoading(false); setNbChartsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setQueriesLoading(true);
      setRows([]);
      // DEMO: Synthetic monthly query table for Skechers for the specific brand
      if (DEMO_BRAND_IDS_SC.has(brandId)) {
        const months = [];
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
          const dt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
          months.push(dt.toISOString().slice(0, 10));
        }
        // Seed totals rows (fallback path uses rows to compute KPIs)
        const seedRows = months.map((m, idx) => {
          const clicks = 9000 + idx * 700;
          const impressions = 380000 + idx * 24000;
          const position = 7.5 - Math.min(3.0, idx * 0.12);
          return { month: m, clicks, impressions, position };
        });
        setRows(seedRows);
        // Non-brand agg
        const nbAgg = months.map((m, idx) => ({
          month: m,
          clicks: Math.round((9000 + idx * 700) * 0.55),
          impressions: Math.round((380000 + idx * 24000) * 0.58),
        }));
        setNbMonthlyAgg(nbAgg);
        // Top queries and monthly position cells
        const queries = [
          'ayakkabı', 'spor ayakkabı', 'koşu ayakkabısı', 'yürüyüş ayakkabısı',
          'beyaz spor ayakkabı', 'ortopedik ayakkabı', 'memory foam ayakkabı',
          'erkek spor ayakkabı', 'kadın spor ayakkabı', 'outlet ayakkabı',
          'sneaker', 'koşu sneaker', 'yürüyüş sneaker', 'air spor ayakkabı', 'bantlı spor ayakkabı'
        ];
        // Build queryAgg with last 12 months; clicks scale down per query
        const queryAggDemo = Object.create(null);
        const queryTotalsDemo = Object.create(null);
        queries.forEach((q, qi) => {
          queryAggDemo[q] = Object.create(null);
          queryTotalsDemo[q] = { clicks: 0, impressions: 0 };
          months.forEach((m, idx) => {
            const baseClicks = 5000 + idx * 400;
            const baseImpr = 180000 + idx * 12000;
            const factor = Math.max(0.15, 0.95 - qi * 0.05); // lower-ranked queries smaller
            const clicks = Math.round(baseClicks * factor);
            const impressions = Math.round(baseImpr * factor * (qi < 3 ? 1.0 : 0.85));
            const pos = 5.2 + (qi * 0.25) - Math.min(1.6, idx * 0.08); // top queries better pos & improve
            queryAggDemo[q][m] = { clicks, impressions, posW: impressions * pos };
            queryTotalsDemo[q].clicks += clicks;
            queryTotalsDemo[q].impressions += impressions;
          });
        });
        setQueryMonths(months);
        // pick top 25 from our seed, already ordered by declaration
        setTopQueries(queries.slice(0, 25));
        setQueryAgg(queryAggDemo);
        setQueryTotals(queryTotalsDemo);
        // Seed brand terms so useMemos produce the same demo split as before
        // (everything containing "ayakkabı" counts as brand).
        if (!brandTermsInitialized) {
          let saved = null;
          try { saved = JSON.parse(localStorage.getItem(`sc_brand_terms_${brandId}`) || 'null'); } catch {}
          setBrandTerms(Array.isArray(saved) ? saved : ['ayakkabı']);
          setBrandTermsInitialized(true);
        }
        setLoading(false);
        setQueriesLoading(false);
        return;
      }
      // Fetch ALL data (wide range)
      const startDate = '2000-01-01';
      const d = new Date(); d.setDate(d.getDate() - 1);
      const endDate = d.toISOString().slice(0, 10);
      const params = new URLSearchParams({ startDate, endDate, limit: '1000000' });
      const [resTotals] = await Promise.all([
        fetch(`https://api.glowytics.com/api/google-ads/search-console/totals-daily?${params}`, {
          credentials: 'include',
          headers: baseHeaders
        })
      ]);
      const jsTotals = await resTotals.json();
      if (!resTotals.ok) throw new Error(jsTotals.error || 'Failed to fetch totals daily');
      // Paginate totals as well to avoid oversized responses/timeouts
      const totalsArr = [];
      {
        const pageSize = 5000;
        let offsetT = 0;
        while (true) {
          const tp = new URLSearchParams({ startDate, endDate, limit: String(pageSize), offset: String(offsetT) });
          const resT = await fetch(`https://api.glowytics.com/api/google-ads/search-console/totals-daily?${tp}`, {
            credentials: 'include',
            headers: baseHeaders
          });
          const jsT = await resT.json();
          if (!resT.ok) throw new Error(jsT.error || 'Failed to fetch totals daily page');
          const rowsT = Array.isArray(jsT.rows) ? jsT.rows : [];
          totalsArr.push(...rowsT);
          if (rowsT.length < pageSize) break;
          offsetT += rowsT.length;
        }
      }
      // Paginate query-daily to bypass backend 5000 cap
      const qArr = [];
      const pageSize = 5000;
      let offset = 0;
      // Order by date DESC in API; we don't care as we aggregate
      // Fetch until fewer than pageSize rows returned
      while (true) {
        const qp = new URLSearchParams({ startDate, endDate, limit: String(pageSize), offset: String(offset) });
        const resPage = await fetch(`https://api.glowytics.com/api/google-ads/search-console/query-daily?${qp}`, {
          credentials: 'include',
          headers: baseHeaders
        });
        const jsPage = await resPage.json();
        if (!resPage.ok) throw new Error(jsPage.error || 'Failed to fetch query daily page');
        const rows = Array.isArray(jsPage.rows) ? jsPage.rows : [];
        qArr.push(...rows);
        if (rows.length < pageSize) break;
        offset += rows.length;
      }
      // normalize dates
      const norm = totalsArr.map(r => ({ ...r, date: typeof r.date === 'string' ? r.date.slice(0, 10) : r.date }));
      setRows(norm);
      // Build query -> month aggregate for table + all-time per-query totals
      const qNorm = qArr.map(r => ({ ...r, date: typeof r.date === 'string' ? r.date.slice(0, 10) : r.date, query: String(r.query || '') }));
      const monthsSet = new Set();
      const aggObj = Object.create(null); // { [query]: { [month]: {clicks, impressions, posW} } }
      const totalsObj = Object.create(null); // { [query]: { clicks, impressions } }
      for (const r of qNorm) {
        const m = monthKey(r.date);
        const q = r.query || '';
        monthsSet.add(m);
        if (!aggObj[q]) aggObj[q] = Object.create(null);
        if (!aggObj[q][m]) aggObj[q][m] = { clicks: 0, impressions: 0, posW: 0 };
        const c = Number(r.clicks || 0);
        const i = Number(r.impressions || 0);
        aggObj[q][m].clicks += c;
        aggObj[q][m].impressions += i;
        aggObj[q][m].posW += i * Number(r.position || 0);
        if (!totalsObj[q]) totalsObj[q] = { clicks: 0, impressions: 0 };
        totalsObj[q].clicks += c;
        totalsObj[q].impressions += i;
      }
      const monthsSortedDesc = [...monthsSet].sort((a, b) => (a > b ? -1 : 1));
      const top25 = Object.entries(totalsObj)
        .sort((a, b) => (b[1].clicks || 0) - (a[1].clicks || 0))
        .slice(0, 25)
        .map(([q]) => q);
      setQueryMonths(monthsSortedDesc);
      setQueryAgg(aggObj);
      setQueryTotals(totalsObj);
      setTopQueries(top25);

      // Seed brand terms on first load: saved -> auto-detect from top queries.
      // Done inline (not in a separate effect) so the initial paint of the
      // Non-Brand sections uses the correct filter without a flicker.
      if (!brandTermsInitialized) {
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(`sc_brand_terms_${brandId}`) || 'null'); } catch {}
        if (Array.isArray(saved)) {
          setBrandTerms(saved);
        } else {
          setBrandTerms(autoDetectBrandTerms(totalsObj));
        }
        setBrandTermsInitialized(true);
      }
    } catch (e) {
      console.error('[MonthlyQueries] fetch error:', e);
    } finally {
      setLoading(false);
      setQueriesLoading(false);
    }
  };

  // Landing pages behind the top non-brand queries. This needs the page+query
  // breakdown, which only Postgres can aggregate at a usable size — the paired
  // table is far larger than the single-dimension ones, so it is summed
  // server-side rather than paged into the browser like the tables above.
  const fetchQueryPages = async (startDate, endDate, terms) => {
    try {
      setQpLoading(true);
      setQpError(null);
      setQpUnavailable(false);
      setQpRange({ startDate, endDate });

      if (DEMO_BRAND_IDS_SC.has(brandId)) {
        setQpRows(buildDemoQueryPages());
        setQpCoverage({ minDate: startDate, maxDate: endDate });
        setQpLoading(false);
        return;
      }

      const params = new URLSearchParams({
        startDate, endDate, limit: '25', pagesPerQuery: '5', sortBy: 'impressions'
      });
      if (terms && terms.length) params.set('brandTerms', terms.join(','));
      const res = await fetch(`https://api.glowytics.com/api/search-console/query-pages?${params}`, {
        credentials: 'include',
        headers: baseHeaders
      });
      const js = await res.json();
      if (!res.ok) throw new Error(js.error || 'Failed to fetch query pages');
      setQpRows(Array.isArray(js.rows) ? js.rows : []);
      setQpCoverage(js.coverage || { minDate: null, maxDate: null });
      setQpUnavailable(!!js.unavailable);
    } catch (e) {
      console.error('[MonthlyQueries] fetchQueryPages error:', e);
      setQpError(e.message || 'Failed to load');
      setQpRows([]);
    } finally {
      setQpLoading(false);
    }
  };

  useEffect(() => {
    if (brandId) fetchData();
  }, [brandId]);

  // Covers the same last-6-months window as the tables above, but derives it
  // from the calendar rather than from `queryMonths`. `queryMonths` is only
  // filled once the query-daily pagination finishes, which on a large site is
  // millions of rows and minutes of waiting — this section aggregates
  // server-side and has no reason to wait for it.
  useEffect(() => {
    if (!brandId || !brandTermsInitialized) return;
    const end = new Date(); end.setDate(end.getDate() - 1);
    const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - 5, 1));
    fetchQueryPages(start.toISOString().slice(0, 10), end.toISOString().slice(0, 10), brandTerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, brandTermsInitialized, brandTerms]);

  useEffect(() => {
    if (brandId) fetchChartsQuick();
  }, [brandId]);

  // Reset brand-term initialization when switching brands, then re-seed from
  // storage straight away when this brand has saved terms. Waiting for
  // `fetchData` to do it would hold the server-side sections behind the
  // query-daily pagination; only auto-detection genuinely needs that data.
  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(`sc_brand_terms_${brandId}`) || 'null'); } catch {}
    if (Array.isArray(saved)) {
      setBrandTerms(saved);
      setBrandTermsInitialized(true);
    } else {
      setBrandTermsInitialized(false);
      setBrandTerms([]);
    }
  }, [brandId]);

  // Persist brand terms per brand after first init
  useEffect(() => {
    if (!brandId || !brandTermsInitialized) return;
    try { localStorage.setItem(`sc_brand_terms_${brandId}`, JSON.stringify(brandTerms)); } catch {}
  }, [brandTerms, brandTermsInitialized, brandId]);

  // Brand term editor handlers
  const addBrandTerm = (raw) => {
    const v = String(raw || '').trim().toLowerCase();
    if (!v) return;
    if (brandTerms.some(t => t.toLowerCase() === v)) return;
    setBrandTerms([...brandTerms, v]);
  };
  const removeBrandTerm = (t) => {
    setBrandTerms(brandTerms.filter(x => x !== t));
  };
  const resetBrandTermsAuto = () => {
    const auto = autoDetectBrandTerms(queryTotals);
    setBrandTerms(auto);
  };

  // Compiled regex for the current brand terms — re-used across useMemos below
  const brandRegex = useMemo(() => buildBrandRegex(brandTerms), [brandTerms]);

  // Non-brand monthly aggregation: derived from queryAgg + brandTerms when loaded,
  // falls back to the quick-paint backend value while data is still loading.
  const nbMonthlyAggDisplay = useMemo(() => {
    const hasFull = queryAgg && Object.keys(queryAgg).length > 0;
    if (!hasFull) return nbMonthlyAgg;
    const byMonth = new Map();
    for (const q of Object.keys(queryAgg)) {
      if (isBrandQuery(q, brandRegex)) continue;
      const months = queryAgg[q] || {};
      for (const m of Object.keys(months)) {
        const cell = months[m];
        if (!cell) continue;
        if (!byMonth.has(m)) byMonth.set(m, { clicks: 0, impressions: 0 });
        const prev = byMonth.get(m);
        byMonth.set(m, {
          clicks: prev.clicks + Number(cell.clicks || 0),
          impressions: prev.impressions + Number(cell.impressions || 0),
        });
      }
    }
    return [...byMonth.keys()]
      .sort((a, b) => (a > b ? -1 : 1))
      .map(m => ({ month: m, ...byMonth.get(m) }));
  }, [queryAgg, brandRegex, nbMonthlyAgg]);

  // Top non-brand queries ranked by total clicks (existing "Monthly Avg Position" table)
  const topNonBrandQueries = useMemo(() => {
    const items = [];
    for (const q of Object.keys(queryTotals || {})) {
      if (isBrandQuery(q, brandRegex)) continue;
      items.push([q, Number(queryTotals[q].clicks || 0)]);
    }
    return items.sort((a, b) => b[1] - a[1]).slice(0, 25).map(([q]) => q);
  }, [queryTotals, brandRegex]);

  // Top non-brand queries ranked by total impressions (NEW "Monthly Impressions" table)
  const topNonBrandQueriesByImpr = useMemo(() => {
    const items = [];
    for (const q of Object.keys(queryTotals || {})) {
      if (isBrandQuery(q, brandRegex)) continue;
      items.push([q, Number(queryTotals[q].impressions || 0)]);
    }
    return items.sort((a, b) => b[1] - a[1]).slice(0, 25).map(([q]) => q);
  }, [queryTotals, brandRegex]);

  // Monthly aggregation totals
  const { months, monthlyAgg, overall } = useMemo(() => {
    const byMonth = new Map(); // month -> { clicks, impressions }
    let clicks = 0, impr = 0, posW = 0;
    for (const r of rows) {
      const m = r.month ? (typeof r.month === 'string' ? r.month.slice(0, 10) : r.month) : monthKey(r.date);
      const c = Number(r.clicks || 0);
      const i = Number(r.impressions || 0);
      const p = Number(r.position || 0);
      if (!byMonth.has(m)) byMonth.set(m, { clicks: 0, impressions: 0 });
      const prev = byMonth.get(m);
      byMonth.set(m, { clicks: prev.clicks + c, impressions: prev.impressions + i });
      clicks += c; impr += i; posW += i * p;
    }
    const months = [...byMonth.keys()].sort((a, b) => (a > b ? -1 : 1));
    const monthlyAgg = months.map(m => ({ month: m, ...byMonth.get(m) }));
    const overall = {
      clicks,
      impressions: impr,
      ctr: impr > 0 ? clicks / impr : 0,
      position: impr > 0 ? posW / impr : 0
    };
    return { months, monthlyAgg, overall };
  }, [rows]);

  // Line chart data
  const clicksChart = useMemo(() => ({
    labels: (monthlyAgg || []).map(r => formatMonthHeader(r.month)).reverse(),
    datasets: [
      {
        label: 'Clicks',
        data: (monthlyAgg || []).map(r => r.clicks).reverse(),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.12)',
        tension: 0.35,
        fill: true
      }
    ]
  }), [monthlyAgg]);
  const imprChart = useMemo(() => ({
    labels: (monthlyAgg || []).map(r => formatMonthHeader(r.month)).reverse(),
    datasets: [
      {
        label: 'Impressions',
        data: (monthlyAgg || []).map(r => r.impressions).reverse(),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.12)',
        tension: 0.35,
        fill: true
      }
    ]
  }), [monthlyAgg]);

  // Non-brand charts (driven by smart-filtered aggregation)
  const nbClicksChart = useMemo(() => ({
    labels: (nbMonthlyAggDisplay || []).map(r => formatMonthHeader(r.month)).reverse(),
    datasets: [
      {
        label: 'Non-Brand Clicks',
        data: (nbMonthlyAggDisplay || []).map(r => r.clicks).reverse(),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.12)',
        tension: 0.35,
        fill: true
      }
    ]
  }), [nbMonthlyAggDisplay]);
  const nbImprChart = useMemo(() => ({
    labels: (nbMonthlyAggDisplay || []).map(r => formatMonthHeader(r.month)).reverse(),
    datasets: [
      {
        label: 'Non-Brand Impressions',
        data: (nbMonthlyAggDisplay || []).map(r => r.impressions).reverse(),
        borderColor: 'rgb(234, 179, 8)',
        backgroundColor: 'rgba(234, 179, 8, 0.12)',
        tension: 0.35,
        fill: true
      }
    ]
  }), [nbMonthlyAggDisplay]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <img src="/logo_search_consolev44.svg" alt="Search Console" className="h-12 md:h-14 w-auto" />
          <h1 className="text-2xl font-semibold">Monthly Queries</h1>
        </div>
        <div />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm text-gray-500">Clicks</div>
          <div className="text-2xl font-semibold mt-1">{formatNumber(overall.clicks)}</div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm text-gray-500">Impressions</div>
          <div className="text-2xl font-semibold mt-1">{formatNumber(overall.impressions)}</div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm text-gray-500">CTR</div>
          <div className="text-2xl font-semibold mt-1">{fmtPct(overall.ctr)}</div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm text-gray-500">Avg Position</div>
          <div className="text-2xl font-semibold mt-1">{Number(overall.position || 0).toFixed(2)}</div>
        </div>
      </div>

      {/* Dual monthly line charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700">Monthly Clicks</div>
          <div className="mt-2">
            {chartsLoading ? (
              <div className="h-[260px] rounded bg-gray-100 animate-pulse" />
            ) : (
              <LineChart
                data={clicksChart}
                options={{
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } }
                }}
                height={260}
              />
            )}
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700">Monthly Impressions</div>
          <div className="mt-2">
            {chartsLoading ? (
              <div className="h-[260px] rounded bg-gray-100 animate-pulse" />
            ) : (
              <LineChart
                data={imprChart}
                options={{
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } }
                }}
                height={260}
              />
            )}
          </div>
        </div>
      </div>

      {/* Brand Terms Editor — drives all Non-Brand calculations below */}
      <div className="bg-white border rounded-xl p-4 mb-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-700">Brand Terms</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Queries starting with any of these terms (word-prefix match) are classified as <b>Brand</b>. Everything else is counted as <b>Non-Brand</b>. Edit to fine-tune — changes are saved for this brand.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={resetBrandTermsAuto}
              className="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
              title="Re-run auto detection from top queries"
            >
              Auto-detect
            </button>
            <button
              type="button"
              onClick={() => setEditingBrand(v => !v)}
              className="px-2.5 py-1 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              {editingBrand ? 'Done' : 'Edit'}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {brandTerms.length === 0 && !editingBrand && (
            <span className="text-xs text-gray-400">No brand terms set — every query counts as Non-Brand.</span>
          )}
          {brandTerms.map(t => (
            <span
              key={t}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
            >
              {t}
              {editingBrand && (
                <button
                  type="button"
                  onClick={() => removeBrandTerm(t)}
                  className="ml-1 text-blue-500 hover:text-blue-700 leading-none"
                  aria-label={`Remove ${t}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {editingBrand && (
            <form
              className="inline-flex items-center gap-1"
              onSubmit={(e) => { e.preventDefault(); addBrandTerm(newBrandTerm); setNewBrandTerm(''); }}
            >
              <input
                type="text"
                value={newBrandTerm}
                onChange={(e) => setNewBrandTerm(e.target.value)}
                placeholder="Add term…"
                className="px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button type="submit" className="px-2 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700">Add</button>
            </form>
          )}
        </div>
      </div>

      {/* Non-Brand Dual monthly line charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700">Non-Brand Monthly Clicks</div>
          <div className="mt-2">
            {nbChartsLoading ? (
              <div className="h-[260px] rounded bg-gray-100 animate-pulse" />
            ) : (
              <LineChart
                data={nbClicksChart}
                options={{
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } }
                }}
                height={260}
              />
            )}
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700">Non-Brand Monthly Impressions</div>
          <div className="mt-2">
            {nbChartsLoading ? (
              <div className="h-[260px] rounded bg-gray-100 animate-pulse" />
            ) : (
              <LineChart
                data={nbImprChart}
                options={{
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } }
                }}
                height={260}
              />
            )}
          </div>
        </div>
      </div>

      {/* Top Queries - Monthly Position (last 6 months) */}
      <div className="bg-white border rounded-xl p-4">
        <div className="text-sm font-medium text-gray-700 mb-2">Top Queries — Monthly Avg Position</div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          {queriesLoading ? (
            <div className="h-40 rounded bg-gray-100 animate-pulse" />
          ) : (
            <table className="min-w-full text-sm relative">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-2 bg-gray-50">Query</th>
                  {(queryMonths.slice(0, 6).reverse()).map(m => (
                    <th key={m} className="text-right p-2 bg-gray-50">{formatMonthHeader(m)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topQueries.slice(0, 15).map((q, i) => (
                  <tr key={q} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2">{q}</td>
                    {(queryMonths.slice(0, 6).reverse()).map(m => {
                      const cell = (queryAgg?.[q]?.[m]) || null;
                      const pos = cell && cell.impressions > 0 ? (cell.posW / cell.impressions) : null;
                      return (
                        <td key={m} className="p-2 text-right">{pos == null ? '-' : Number(pos).toFixed(2)}</td>
                      );
                    })}
                  </tr>
                ))}
                {topQueries.length > 15 && topQueries.slice(15).map((q, i) => (
                  <tr key={q} className={(i + 15) % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2">{q}</td>
                    {(queryMonths.slice(0, 6).reverse()).map(m => {
                      const cell = (queryAgg?.[q]?.[m]) || null;
                      const pos = cell && cell.impressions > 0 ? (cell.posW / cell.impressions) : null;
                      return (
                        <td key={m} className="p-2 text-right">{pos == null ? '-' : Number(pos).toFixed(2)}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Top Non-Brand Queries - Monthly Position (last 6 months) */}
      <div className="bg-white border rounded-xl p-4 mt-4">
        <div className="text-sm font-medium text-gray-700 mb-2">Top Non-Brand Queries — Monthly Avg Position</div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          {queriesLoading ? (
            <div className="h-40 rounded bg-gray-100 animate-pulse" />
          ) : (
            <table className="min-w-full text-sm relative">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-2 bg-gray-50">Query</th>
                  {(queryMonths.slice(0, 6).reverse()).map(m => (
                    <th key={m} className="text-right p-2 bg-gray-50">{formatMonthHeader(m)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topNonBrandQueries.slice(0, 15).map((q, i) => (
                  <tr key={q} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2">{q}</td>
                    {(queryMonths.slice(0, 6).reverse()).map(m => {
                      const cell = (queryAgg?.[q]?.[m]) || null;
                      const pos = cell && cell.impressions > 0 ? (cell.posW / cell.impressions) : null;
                      return (
                        <td key={m} className="p-2 text-right">{pos == null ? '-' : Number(pos).toFixed(2)}</td>
                      );
                    })}
                  </tr>
                ))}
                {topNonBrandQueries.length > 15 && topNonBrandQueries.slice(15).map((q, i) => (
                  <tr key={q} className={(i + 15) % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2">{q}</td>
                    {(queryMonths.slice(0, 6).reverse()).map(m => {
                      const cell = (queryAgg?.[q]?.[m]) || null;
                      const pos = cell && cell.impressions > 0 ? (cell.posW / cell.impressions) : null;
                      return (
                        <td key={m} className="p-2 text-right">{pos == null ? '-' : Number(pos).toFixed(2)}</td>
                      );
                    })}
                  </tr>
                ))}
                {topNonBrandQueries.length === 0 && (
                   <tr>
                     <td colSpan={7} className="p-4 text-center text-gray-500">No non-brand queries found.</td>
                   </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Top Non-Brand Queries - Monthly Impressions (last 6 months) */}
      <div className="bg-white border rounded-xl p-4 mt-4">
        <div className="text-sm font-medium text-gray-700 mb-2">Top Non-Brand Queries — Monthly Impressions</div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          {queriesLoading ? (
            <div className="h-40 rounded bg-gray-100 animate-pulse" />
          ) : (() => {
            const visibleMonths = queryMonths.slice(0, 6).reverse();
            const rowsToRender = topNonBrandQueriesByImpr.slice(0, 25);
            // Top-25 column totals across the 6 visible months
            const colTotals = visibleMonths.map(m =>
              rowsToRender.reduce((acc, q) => acc + Number(queryAgg?.[q]?.[m]?.impressions || 0), 0)
            );
            const grandTotal = colTotals.reduce((a, b) => a + b, 0);
            // Full non-brand month totals (every non-brand query, not just the top 25)
            const nbMap = new Map(
              (nbMonthlyAggDisplay || []).map(r => [String(r.month).slice(0, 10), Number(r.impressions || 0)])
            );
            const allNbColTotals = visibleMonths.map(m => nbMap.get(m) || 0);
            const allNbGrandTotal = allNbColTotals.reduce((a, b) => a + b, 0);
            return (
              <table className="min-w-full text-sm relative">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-2 bg-gray-50">Query</th>
                    {visibleMonths.map(m => (
                      <th key={m} className="text-right p-2 bg-gray-50">{formatMonthHeader(m)}</th>
                    ))}
                    <th className="text-right p-2 bg-gray-50 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsToRender.map((q, i) => {
                    // Sum only across the 6 visible months so the Total column matches what the user sees
                    const rowTotal = visibleMonths.reduce(
                      (acc, m) => acc + Number(queryAgg?.[q]?.[m]?.impressions || 0),
                      0
                    );
                    return (
                      <tr key={q} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-2">{q}</td>
                        {visibleMonths.map(m => {
                          const cell = (queryAgg?.[q]?.[m]) || null;
                          const impr = cell ? Number(cell.impressions || 0) : 0;
                          return (
                            <td key={m} className="p-2 text-right">{impr > 0 ? formatNumber(impr) : '-'}</td>
                          );
                        })}
                        <td className="p-2 text-right font-semibold">{rowTotal > 0 ? formatNumber(rowTotal) : '-'}</td>
                      </tr>
                    );
                  })}
                  {rowsToRender.length === 0 && (
                    <tr>
                      <td colSpan={visibleMonths.length + 2} className="p-4 text-center text-gray-500">No non-brand queries found.</td>
                    </tr>
                  )}
                </tbody>
                {rowsToRender.length > 0 && (
                  <tfoot className="bg-gray-100 sticky bottom-0">
                    <tr className="border-t border-gray-300">
                      <td className="p-2 font-medium text-gray-600 text-xs">Top 25 Total</td>
                      {colTotals.map((t, idx) => (
                        <td key={idx} className="p-2 text-right font-medium text-gray-600 text-xs">
                          {t > 0 ? formatNumber(t) : '-'}
                        </td>
                      ))}
                      <td className="p-2 text-right font-medium text-gray-700 text-xs">
                        {grandTotal > 0 ? formatNumber(grandTotal) : '-'}
                      </td>
                    </tr>
                    <tr className="bg-gray-200">
                      <td className="p-2 font-semibold text-gray-800">Month Total (All Non-Brand)</td>
                      {allNbColTotals.map((t, idx) => (
                        <td key={idx} className="p-2 text-right font-semibold text-gray-800">
                          {t > 0 ? formatNumber(t) : '-'}
                        </td>
                      ))}
                      <td className="p-2 text-right font-semibold text-gray-900">
                        {allNbGrandTotal > 0 ? formatNumber(allNbGrandTotal) : '-'}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            );
          })()}
        </div>
      </div>

      {/* Top Non-Brand Queries - Monthly Clicks (last 6 months) */}
      <div className="bg-white border rounded-xl p-4 mt-4">
        <div className="text-sm font-medium text-gray-700 mb-2">Top Non-Brand Queries — Monthly Clicks</div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          {queriesLoading ? (
            <div className="h-40 rounded bg-gray-100 animate-pulse" />
          ) : (() => {
            const visibleMonths = queryMonths.slice(0, 6).reverse();
            const rowsToRender = topNonBrandQueries.slice(0, 25);
            // Top-25 column totals across the 6 visible months
            const colTotals = visibleMonths.map(m =>
              rowsToRender.reduce((acc, q) => acc + Number(queryAgg?.[q]?.[m]?.clicks || 0), 0)
            );
            const grandTotal = colTotals.reduce((a, b) => a + b, 0);
            // Full non-brand month totals (every non-brand query, not just the top 25)
            const nbMap = new Map(
              (nbMonthlyAggDisplay || []).map(r => [String(r.month).slice(0, 10), Number(r.clicks || 0)])
            );
            const allNbColTotals = visibleMonths.map(m => nbMap.get(m) || 0);
            const allNbGrandTotal = allNbColTotals.reduce((a, b) => a + b, 0);
            return (
              <table className="min-w-full text-sm relative">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-2 bg-gray-50">Query</th>
                    {visibleMonths.map(m => (
                      <th key={m} className="text-right p-2 bg-gray-50">{formatMonthHeader(m)}</th>
                    ))}
                    <th className="text-right p-2 bg-gray-50 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsToRender.map((q, i) => {
                    // Sum only across the 6 visible months so the Total column matches what the user sees
                    const rowTotal = visibleMonths.reduce(
                      (acc, m) => acc + Number(queryAgg?.[q]?.[m]?.clicks || 0),
                      0
                    );
                    return (
                      <tr key={q} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-2">{q}</td>
                        {visibleMonths.map(m => {
                          const cell = (queryAgg?.[q]?.[m]) || null;
                          const clicks = cell ? Number(cell.clicks || 0) : 0;
                          return (
                            <td key={m} className="p-2 text-right">{clicks > 0 ? formatNumber(clicks) : '-'}</td>
                          );
                        })}
                        <td className="p-2 text-right font-semibold">{rowTotal > 0 ? formatNumber(rowTotal) : '-'}</td>
                      </tr>
                    );
                  })}
                  {rowsToRender.length === 0 && (
                    <tr>
                      <td colSpan={visibleMonths.length + 2} className="p-4 text-center text-gray-500">No non-brand queries found.</td>
                    </tr>
                  )}
                </tbody>
                {rowsToRender.length > 0 && (
                  <tfoot className="bg-gray-100 sticky bottom-0">
                    <tr className="border-t border-gray-300">
                      <td className="p-2 font-medium text-gray-600 text-xs">Top 25 Total</td>
                      {colTotals.map((t, idx) => (
                        <td key={idx} className="p-2 text-right font-medium text-gray-600 text-xs">
                          {t > 0 ? formatNumber(t) : '-'}
                        </td>
                      ))}
                      <td className="p-2 text-right font-medium text-gray-700 text-xs">
                        {grandTotal > 0 ? formatNumber(grandTotal) : '-'}
                      </td>
                    </tr>
                    <tr className="bg-gray-200">
                      <td className="p-2 font-semibold text-gray-800">Month Total (All Non-Brand)</td>
                      {allNbColTotals.map((t, idx) => (
                        <td key={idx} className="p-2 text-right font-semibold text-gray-800">
                          {t > 0 ? formatNumber(t) : '-'}
                        </td>
                      ))}
                      <td className="p-2 text-right font-semibold text-gray-900">
                        {allNbGrandTotal > 0 ? formatNumber(allNbGrandTotal) : '-'}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            );
          })()}
        </div>
      </div>

      {/* Landing pages behind the top non-brand queries */}
      <div className="bg-white border rounded-xl p-4 mt-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-sm font-medium text-gray-700">
              Top Non-Brand Queries — Landing Pages
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              The 25 non-brand queries with the most impressions, and the top 5 pages each one
              actually sent traffic to.
              {qpRange.startDate && (
                <> Range: {fmtDateShort(qpRange.startDate)} – {fmtDateShort(qpRange.endDate)}.</>
              )}
            </div>
          </div>
          {qpRows.length > 0 && (
            <button
              type="button"
              onClick={() => setQpCollapsed(
                qpCollapsed.size ? new Set() : new Set(qpRows.map(r => r.query))
              )}
              className="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0"
            >
              {qpCollapsed.size ? 'Expand all' : 'Collapse all'}
            </button>
          )}
        </div>

        {/* The page+query breakdown is backfilled separately from the
            single-dimension tables, so it can cover far less history than the
            months shown above. Say so rather than let a short bar read as a
            traffic drop. */}
        {!qpLoading && !qpUnavailable && qpCoverage.minDate && qpRange.startDate &&
          qpCoverage.minDate > qpRange.startDate && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
            Query-to-page data only exists from <b>{fmtDateShort(qpCoverage.minDate)}</b> to{' '}
            <b>{fmtDateShort(qpCoverage.maxDate)}</b> for this brand, so this table covers a
            shorter period than the monthly tables above. The numbers here are not comparable
            to them.
          </div>
        )}

        <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
          {qpLoading ? (
            <div className="h-40 rounded bg-gray-100 animate-pulse" />
          ) : qpError ? (
            <div className="p-4 text-center text-sm text-red-600">{qpError}</div>
          ) : qpUnavailable ? (
            <div className="p-4 text-center text-sm text-gray-500">
              This brand has no query-to-page breakdown collected yet.
            </div>
          ) : qpRows.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">No non-brand queries found.</div>
          ) : (
            <table className="min-w-full text-sm relative">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-2 bg-gray-50">Query / Landing Page</th>
                  <th className="text-right p-2 bg-gray-50">Impressions</th>
                  <th className="text-right p-2 bg-gray-50">Share</th>
                  <th className="text-right p-2 bg-gray-50">Clicks</th>
                  <th className="text-right p-2 bg-gray-50">CTR</th>
                  <th className="text-right p-2 bg-gray-50">Avg Pos</th>
                </tr>
              </thead>
              <tbody>
                {qpRows.map((r) => {
                  const collapsed = qpCollapsed.has(r.query);
                  const shown = r.pages || [];
                  const rest = Math.max(0, Number(r.pageCount || 0) - shown.length);
                  const origin = dominantOrigin(shown);
                  return (
                    <React.Fragment key={r.query}>
                      <tr className="bg-gray-100 border-t border-gray-200">
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={() => {
                              const next = new Set(qpCollapsed);
                              if (next.has(r.query)) next.delete(r.query); else next.add(r.query);
                              setQpCollapsed(next);
                            }}
                            className="flex items-center gap-1.5 text-left font-medium text-gray-800 hover:text-blue-700"
                          >
                            <span className="text-gray-400 text-xs w-3 inline-block">
                              {collapsed ? '▸' : '▾'}
                            </span>
                            {r.query}
                          </button>
                          <div className="text-[11px] text-gray-500 mt-0.5 ml-[18px]">
                            {formatNumber(r.pageCount)} page{Number(r.pageCount) === 1 ? '' : 's'} in total
                            {/* Search Console withholds the page for some
                                impressions, so the per-page rows can add up to
                                less than the query's own total. Only worth
                                showing when the gap is big enough to matter. */}
                            {r.coverage != null && r.coverage < 0.95 && (
                              <span
                                className="ml-2 text-amber-700"
                                title={`Search Console reports ${formatNumber(r.reportedImpressions)} impressions for this query, but only attributes ${formatNumber(r.impressions)} of them to a specific page.`}
                              >
                                · page data covers {fmtPct(r.coverage)} of this query
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-right font-semibold">{formatNumber(r.impressions)}</td>
                        <td className="p-2 text-right text-gray-400">—</td>
                        <td className="p-2 text-right font-semibold">{formatNumber(r.clicks)}</td>
                        <td className="p-2 text-right">{fmtPct(r.ctr)}</td>
                        <td className="p-2 text-right">
                          {r.position == null ? '-' : Number(r.position).toFixed(2)}
                        </td>
                      </tr>
                      {!collapsed && shown.map((p) => (
                        <tr key={r.query + '|' + p.page} className="bg-white border-t border-gray-100">
                          <td className="p-2 pl-8">
                            <a
                              href={p.page}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={p.page}
                              className="text-blue-600 hover:underline break-all"
                            >
                              {shortenPage(p.page, origin)}
                            </a>
                          </td>
                          <td className="p-2 text-right">{formatNumber(p.impressions)}</td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ width: `${Math.min(100, Number(p.share || 0) * 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600 w-11 text-right">
                                {fmtPct(p.share)}
                              </span>
                            </div>
                          </td>
                          <td className="p-2 text-right">{formatNumber(p.clicks)}</td>
                          <td className="p-2 text-right">{fmtPct(p.ctr)}</td>
                          <td className="p-2 text-right">
                            {p.position == null ? '-' : Number(p.position).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      {!collapsed && rest > 0 && (
                        <tr className="bg-white border-t border-gray-100">
                          <td colSpan={6} className="p-2 pl-8 text-[11px] text-gray-400">
                            + {formatNumber(rest)} more page{rest === 1 ? '' : 's'} with fewer impressions
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
